import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, HelpCircle, Ticket, Car, ShieldCheck, 
  Users, Ban, AlertTriangle, MapPin, Calendar
} from 'lucide-react';
import { Card } from './ui/card';
import { images, eventInfo } from '../data/mock';

const FAQPage = () => {
  const prohibitedItems = [
    'Any bag larger than 12" x 6" x 12"',
    'Aerosol cans, including mace, pepper spray, hair spray, etc.',
    'Audio or visual recording devices (with lens more than 6″ in length), including GoPro, Google Glass, etc.',
    'Balloons and other inflatables',
    'Bicycles, skateboards, scooters, roller skates, hoverboards and segways',
    'Commercial audio or video recording equipment',
    'Drones and unmanned aircraft vehicles (UAV)',
    'Fireworks, flares, smoke bombs and devices, pyrotechnics, and any missile-like object',
    'Hard-sided containers, including bottles, cans, flasks, coolers and thermoses',
    'Illegal drugs or drug paraphernalia',
    'Inappropriate or offensive attire or signage, including gang insignia, obscenities, inflammatory statements and any threatening or taunting messages',
    'Laptops, tablets (including iPads and Kindles), personal computers, or two-way radios',
    'Laser lights, strobes, flashlights or any other visual impairment device',
    'Noisemakers of any kind, including air horns, bells, bullhorns, thundersticks, whistles, and vuvuzelas',
    'Non-service animals',
    'Outside food or beverages, including liquids of any kind',
    'Portable chairs, seat cushions, child seats, and strollers',
    'Projectiles, including balls and frisbees',
    'Sticks, poles, clubs, monopods, tripods, selfie sticks, or similar objects',
    'Weapons of any kind, including any sharp objects',
    'Any item deemed by venue to be dangerous, inappropriate or disruptive to other guests\' enjoyment of the event'
  ];

  const codeOfConduct = [
    'Profanity or other offensive language or gestures, whether spoken, written or appearing on apparel',
    'Smoking (including vaporizers, e-cigarettes and smokeless tobacco) outside of designated areas',
    'Intoxication or excessive alcohol consumption or signs of substance impairment that results in irresponsible behavior',
    'Bringing prohibited items into the venue',
    'Throwing of items or liquid',
    'Entering the stage/backstage area at any time',
    'Fighting or other threatening behavior, including verbal or physical harassment',
    'Failure to retain ticket and/or present it to event staff if requested to do so',
    'Resale of tickets at the venue',
    'Violation of state or local laws',
    'Failing to follow instruction of personnel or venue policies'
  ];

  return (
    <div className="min-h-screen bg-gray-950" data-testid="faq-page">
      <Helmet>
        <title>FAQs | Aloha Tequila & Arts Festival 2026</title>
        <meta name="description" content="Frequently asked questions about Aloha Tequila & Arts Festival - event rules, prohibited items, code of conduct, and venue information." />
        <link rel="canonical" href="https://www.alohatequilafest.com/faq" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.alohatequilafest.com/' },
            { '@type': 'ListItem', position: 2, name: 'FAQs', item: 'https://www.alohatequilafest.com/faq' }
          ]
        })}</script>
      </Helmet>

      <section className="relative py-12 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-500/5 via-transparent to-transparent" />
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-pink-400 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-10">
            <img 
              src={images.logo} 
              alt="Aloha Tequila & Arts Festival" 
              className="h-24 sm:h-32 mx-auto mb-6 object-contain"
            />
            <div className="inline-flex items-center gap-2 bg-pink-500/10 backdrop-blur-sm px-4 py-2 rounded-full border border-pink-500/20 mb-4">
              <HelpCircle className="w-4 h-4 text-pink-400" />
              <span className="text-pink-400 font-semibold text-sm">EVENT INFORMATION</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">
              Frequently Asked Questions
            </h1>
            <p className="text-white/60">Please review the event information below</p>
          </div>

          {/* Event Details Banner */}
          <div className="bg-gradient-to-r from-pink-500/20 to-orange-500/20 border border-pink-500/30 rounded-2xl p-4 mb-8 flex flex-wrap justify-center gap-4 text-sm">
            <span className="flex items-center gap-2 text-white">
              <MapPin className="w-4 h-4 text-pink-400" />
              {eventInfo.location}
            </span>
            <span className="flex items-center gap-2 text-white">
              <Calendar className="w-4 h-4 text-orange-400" />
              {eventInfo.date} • {eventInfo.time}
            </span>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-8 text-center">
            <p className="text-amber-300 font-semibold text-sm">
              Please note the following rules and be considerate to others in attendance.
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-6">
            
            {/* Access Privileges */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Access Privileges</h2>
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-3">
                International Market Place is designed for all guests to enjoy. However, there are certain areas of the venue that can only be accessed with a designated ticket or credential.
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-3">
                Guests should keep their ticket stubs with them at all times to avoid any confusion as they enjoy the event and its amenities.
              </p>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-300 font-bold text-sm">
                  21+ ONLY — Must be 21 or older to attend
                </p>
              </div>
            </Card>

            {/* ADA Parking */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Car className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">ADA Parking</h2>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                Parking available onsite for a fee.
              </p>
            </Card>

            {/* Bag Inspection */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Bag Inspection</h2>
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-3">
                It is our responsibility to ensure that reasonable precautions are taken to prevent an incident from occurring during the event. The safety of our Guests is an integral component of our customer service program. All bags are subject to inspection before they are permitted into the venue.
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-3">
                In an effort to enhance public safety and facilitate a faster entry through the gates, we are limiting the size and number of bags allowed.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mb-3">
                <p className="text-orange-300 font-bold text-sm">
                  Each person shall be allowed one bag that does not exceed 12&quot; x 6&quot; x 12&quot; in size.
                </p>
              </div>
              <ul className="text-white/70 text-sm space-y-1">
                <li>• Exceptions will only be made for medically necessary items, after proper inspection.</li>
                <li>• Strollers are allowed, however they must be collapsible without obstructing the views for other guests.</li>
                <li>• Non-approved bags must be returned to vehicle or discarded.</li>
                <li>• Coolers will not be permitted.</li>
                <li>• Guests are encouraged to read the listing of prohibited items before arriving.</li>
                <li>• Guests should also arrive early to allow additional time to enter.</li>
              </ul>
              <p className="text-white/50 text-xs mt-3 italic">
                We will not be responsible for any personal items left at the gate.
              </p>
            </Card>

            {/* Code of Conduct */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Code of Conduct</h2>
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                We are committed to creating a safe, comfortable and enjoyable experience. We want fans of all ages to enjoy the excitement and enthusiasm of the show. All fans are encouraged to respect the rights of others by displaying proper etiquette.
              </p>
              <p className="text-white font-semibold text-sm mb-3">
                In order to ensure a safe and enjoyable environment, we ask that fans refrain from the following behavior:
              </p>
              <ul className="space-y-2 mb-4">
                {codeOfConduct.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                    <Ban className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-300 text-sm">
                  <strong>Warning:</strong> Event patrons and guests who choose not to adhere to these provisions will be subject to ejection without refund, loss of ticket privileges, and possible arrest and prosecution for violations. Failure to comply may result in ejection or arrest.
                </p>
                <p className="text-white/60 text-xs mt-2">
                  Patrons are encouraged to report inappropriate behavior to management personnel.
                </p>
              </div>
            </Card>

            {/* Prohibited Items */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Prohibited Items</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {prohibitedItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-white/70 text-xs sm:text-sm bg-white/5 rounded-lg p-2">
                    <Ban className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Final Notice */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
              <p className="text-white/70 text-sm mb-2">
                All patrons and items entering venue are subject to search. Refusal of any guest will result in non-admittance into event.
              </p>
              <p className="text-white/50 text-xs">
                Management is not responsible for items left behind at the entries or items lost, damaged, or stolen.
              </p>
            </div>

          </div>

          {/* Back to Home Button */}
          <div className="text-center mt-10">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
